import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ChevronLeft, Users, ChevronRight, Trash2, Archive, ArchiveRestore, Lock, Unlock, Clock, History, Trophy, Wallet, Target, Download } from "lucide-react";
import { api, apiDownload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BonusBanner } from "@/components/BonusBanner";
import { RegolamentoModal } from "@/components/RegolamentoModal";
import { DeadlineBanner } from "@/components/DeadlineBanner";

export default function Tiket() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [maxEvents, setMaxEvents] = useState(5);
  const [deadline, setDeadline] = useState("");
  const [defaultStake, setDefaultStake] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRooms(await api("/rooms?game=thebesttiket")); } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    try { setHistory(await api("/rooms/history?game=thebesttiket")); }
    catch (e) { toast.error(e.message); setHistory([]); }
  }, []);
  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null) loadHistory();
  };
  const downloadPdf = async (e, roomId, name) => {
    e.stopPropagation();
    try { await apiDownload(`/rooms/${roomId}/premi/pdf`, {}, `premi_${(name || "stanza").replace(/\s+/g, "_")}.pdf`); }
    catch (err) { toast.error(err.message); }
  };

  const join = async () => {
    if (!code.trim()) return;
    try {
      const r = await api("/rooms/join", { method: "POST", body: { invite_code: code.trim() } });
      toast.success("Entrato nella stanza!");
      setCode("");
      navigate(`/tiket/${r.id}`);
    } catch (e) { toast.error(e.message); }
  };

  const create = async () => {
    try {
      const body = { name, description: description.trim() || null, max_events: Number(maxEvents), game: "thebesttiket", default_stake: Number(defaultStake) || 0 };
      if (deadline) body.deadline_at = new Date(deadline).toISOString();
      const r = await api("/rooms", { method: "POST", body });
      toast.success("Stanza creata!");
      setCreating(false); setName(""); setDescription(""); setDeadline(""); setDefaultStake(0);
      navigate(`/tiket/${r.id}`);
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare la stanza "${r.name}"? Verranno rimossi iscritti, schedine e risultati. Operazione irreversibile.`)) return;
    try { await api(`/rooms/${r.id}`, { method: "DELETE" }); toast.success("Stanza eliminata"); load(); }
    catch (err) { toast.error(err.message); }
  };

  const archive = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Archiviare la stanza "${r.name}"? Resterà consultabile nella sezione Archiviate.`)) return;
    try { await api(`/rooms/${r.id}/archive?archived=true`, { method: "POST" }); toast.success("Stanza archiviata"); load(); }
    catch (err) { toast.error(err.message); }
  };
  const unarchive = async (e, r) => {
    e.stopPropagation();
    try { await api(`/rooms/${r.id}/archive?archived=false`, { method: "POST" }); toast.success("Stanza ripristinata"); load(); }
    catch (err) { toast.error(err.message); }
  };

  const toggleOpen = async (e, r) => {
    e.stopPropagation();
    try {
      await api(`/rooms/${r.id}/open-entry`, { method: "PATCH", body: { open: !r.open_entry } });
      toast.success(!r.open_entry ? "Ingresso libero attivato" : "Ingresso libero disattivato");
      load();
    } catch (err) { toast.error(err.message); }
  };
  const joinOpen = async (r) => {
    try {
      await api(`/rooms/${r.id}/join-open`, { method: "POST" });
      toast.success("Entrato nella stanza!");
      navigate(`/tiket/${r.id}`);
    } catch (e) { toast.error(e.message); }
  };

  const activeRooms = rooms.filter((r) => !r.archived);
  const archivedRooms = rooms.filter((r) => r.archived);

  return (
    <div className="space-y-5">
      <button data-testid="tiket-back" onClick={() => navigate("/")} className="flex items-center gap-1 text-[#94A3B8] hover:text-white text-sm transition-colors"><ChevronLeft size={16} /> Hub</button>

      <DeadlineBanner game="thebesttiket" />
      <BonusBanner game="tiket" />

      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-[#F59E0B]">TheBestTicket</h1>
          <p className="text-[#94A3B8] text-sm">Schedine Serie A tra amici. Chi ha la quota più bassa, paga da bere.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RegolamentoModal game="thebesttiket" />
          {isAdmin && (
            <button data-testid="tiket-new" onClick={() => setCreating((v) => !v)} className="text-sm bg-white/10 hover:bg-white/20 border border-white/15 rounded-md px-3 py-2 flex items-center gap-1.5 transition-colors">
              <Plus size={16} /> Nuova
            </button>
          )}
        </div>
      </div>

      {isAdmin && creating && (
        <div className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4 space-y-3">
          <input data-testid="tk-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome stanza" className="w-full bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F59E0B]" />
          <label className="text-xs text-[#94A3B8] block">Descrizione (facoltativa)
            <input data-testid="tk-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="es. G2 di campionato, Champions, Premier League..." maxLength={60} className="mt-1 w-full bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-sm" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-[#94A3B8]">Max eventi
              <input data-testid="tk-maxev" type="number" min="1" value={maxEvents} onChange={(e) => setMaxEvents(e.target.value)} className="mt-1 w-full bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-sm" />
            </label>
            <label className="text-xs text-[#94A3B8]">Puntata di default (€)
              <input data-testid="tk-default-stake" type="number" min="0" step="0.5" value={defaultStake} onChange={(e) => setDefaultStake(e.target.value)} placeholder="0" className="mt-1 w-full bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-sm" />
            </label>
          </div>
          <div className="rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3 space-y-2">
            <div className="text-sm font-bold flex items-center gap-2"><Clock size={16} className="text-[#F59E0B]" /> Scadenza giocate</div>
            <label className="text-xs text-[#94A3B8] block">Data e ora chiusura
              <input data-testid="tk-deadline" type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="mt-1 w-full bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-sm" />
            </label>
          </div>
          <button data-testid="tk-create" onClick={create} disabled={!name} className="bg-[#F59E0B] text-[#1A1000] font-bold text-sm rounded-md px-4 py-2 disabled:opacity-50">Crea stanza</button>
        </div>
      )}

      <div className="flex gap-2">
        <input data-testid="tk-join-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Codice invito" className="flex-1 bg-[#181D22] border border-white/15 rounded-md px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#F59E0B]" />
        <button data-testid="tk-join" onClick={join} className="bg-[#00D95F] text-[#08110A] font-bold rounded-md px-5">Entra</button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#94A3B8]">Caricamento...</div>
      ) : activeRooms.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#181D22] p-8 text-center text-[#94A3B8]">Nessuna stanza. Usa un codice invito per entrare.</div>
      ) : (
        <div className="space-y-3">
          {activeRooms.map((r) => (
            <div key={r.id} data-testid={`tk-card-${r.id}`} className="w-full rounded-xl border border-white/10 bg-[#181D22] p-4 hover:border-[#F59E0B]/60 transition-colors flex items-center gap-3">
              <button onClick={() => ((r.is_member || isAdmin) ? navigate(`/tiket/${r.id}`) : joinOpen(r))} className="flex-1 min-w-0 text-left">
                <div className="text-lg font-extrabold flex items-center gap-2 flex-wrap">
                  {r.name}
                  {r.open_entry && <span data-testid={`tk-open-badge-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#00D95F]/15 text-[#00D95F] border border-[#00D95F]/40"><Unlock size={10} /> Libero</span>}
                  {r.partial && <span data-testid={`tk-partial-badge-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#3B82F6]/15 text-[#93C5FD] border border-[#3B82F6]/40"><Clock size={10} /> Calcolo parziale · in corso</span>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-sm text-[#94A3B8]">
                  {(r.description || r.matchday != null) && <span>{r.description || `Giornata ${r.matchday}`}</span>}
                  {r.members_count != null && <span className="flex items-center gap-1"><Users size={13} /> {r.members_count}</span>}
                  {r.status && <span className={`uppercase text-xs font-bold px-1.5 py-0.5 rounded ${r.status === "settled" ? "text-[#00D95F]" : ""}`}>{r.status === "settled" ? "Concluso" : r.status === "closed" ? "Chiusa" : "Aperta"}</span>}
                </div>
              </button>
              {!isAdmin && !r.is_member && r.open_entry && (
                <button data-testid={`tk-joinopen-${r.id}`} onClick={() => joinOpen(r)} className="bg-[#00D95F] text-[#08110A] text-sm font-bold rounded-md px-3 py-2 shrink-0">Entra</button>
              )}
              {isAdmin && (
                <button data-testid={`tk-openentry-${r.id}`} onClick={(e) => toggleOpen(e, r)} title={r.open_entry ? "Ingresso libero attivo — tocca per richiudere" : "Attiva ingresso libero"} className={`p-2 rounded-md shrink-0 border transition-colors ${r.open_entry ? "text-[#00D95F] border-[#00D95F]/40 bg-[#00D95F]/10" : "text-[#94A3B8] border-white/15 hover:bg-white/10"}`}>
                  {r.open_entry ? <Unlock size={18} /> : <Lock size={18} />}
                </button>
              )}
              {isAdmin && r.status === "settled" && (
                <button data-testid={`tk-archive-${r.id}`} onClick={(e) => archive(e, r)} title="Archivia stanza" className="p-2 rounded-md text-[#F59E0B] hover:bg-[#F59E0B]/10 shrink-0"><Archive size={18} /></button>
              )}
              {isAdmin && (
                <button data-testid={`tk-delete-${r.id}`} onClick={(e) => remove(e, r)} title="Elimina stanza" className="p-2 rounded-md text-[#EF4444] hover:bg-[#EF4444]/10 shrink-0"><Trash2 size={18} /></button>
              )}
              <ChevronRight className="text-[#F59E0B] shrink-0" size={20} />
            </div>
          ))}
        </div>
      )}

      {/* Storico stanze concluse */}
      <div className="space-y-3">
        <button data-testid="tk-history-toggle" onClick={toggleHistory} className="flex items-center gap-2 text-sm font-bold text-[#F59E0B] hover:text-white transition-colors">
          <History size={16} /> Storico stanze concluse {showHistory ? "▲" : "▼"}
        </button>
        {showHistory && (
          history === null ? (
            <div className="py-6 text-center text-[#94A3B8] text-sm">Caricamento storico...</div>
          ) : history.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-[#181D22] p-6 text-center text-[#94A3B8] text-sm">Nessuna stanza conclusa nell'archivio.</div>
          ) : (
            history.map((h) => {
              const winner = h.podium?.[0];
              return (
                <div key={h.room_id} data-testid={`tk-history-${h.room_id}`} className="rounded-xl border border-white/10 bg-[#181D22] p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <button onClick={() => navigate(`/tiket/${h.room_id}`)} className="text-left min-w-0">
                      <div className="font-extrabold text-lg truncate">{h.name}</div>
                      <div className="text-xs text-[#94A3B8]">{h.description || ""}{h.description ? " · " : ""}{h.players_count} giocatori · <span className="text-[#00D95F] uppercase font-bold">Concluso</span></div>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      {h.montepremi > 0 && <span className="inline-flex items-center gap-1 text-sm font-bold text-[#00D95F] bg-[#00D95F]/10 border border-[#00D95F]/30 rounded-full px-3 py-1"><Wallet size={13} /> {h.montepremi.toFixed(2)}€</span>}
                      {h.has_prizes && <button data-testid={`tk-history-pdf-${h.room_id}`} onClick={(e) => downloadPdf(e, h.room_id, h.name)} title="Scarica PDF premi" className="p-2 rounded-md text-[#94A3B8] hover:text-white border border-white/15 hover:bg-white/10"><Download size={15} /></button>}
                    </div>
                  </div>

                  {h.podium?.length > 0 && (
                    <div className="space-y-1.5">
                      {h.podium.map((p) => (
                        <div key={p.rank} className="flex items-center gap-2 text-sm">
                          <span className="w-5 text-[#94A3B8] font-bold">{p.rank}</span>
                          {p.rank === 1 && <Trophy size={14} className="text-[#F59E0B]" />}
                          <span className="flex-1 min-w-0 truncate">{p.nickname}</span>
                          {p.bonus_won && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/40"><Target size={10} /> Bonus</span>}
                          {h.has_prizes && <span className={`font-extrabold ${p.winnings_net > 0 ? "text-[#00D95F]" : "text-[#94A3B8]"}`}>{p.winnings_net > 0 ? `+${p.winnings_net.toFixed(2)}€` : `${(p.payout || 0).toFixed(2)}€`}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {h.bonus?.enabled && h.bonus.result && (
                    <div className="text-xs text-[#10B981] flex items-center gap-1.5 border-t border-white/5 pt-2">
                      <Target size={12} /> Bonus {h.bonus.home}-{h.bonus.away}: <b>{h.bonus.result.home}-{h.bonus.result.away}</b>
                      <span className="text-[#94A3B8]">{h.bonus.winners?.length ? `· ${h.bonus.winners.join(", ")}` : "· nessun vincitore"}</span>
                    </div>
                  )}
                </div>
              );
            })
          )
        )}
      </div>

      {isAdmin && archivedRooms.length > 0 && (
        <div className="space-y-3">
          <button data-testid="tk-archived-toggle" onClick={() => setShowArchived((v) => !v)} className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white transition-colors">
            <Archive size={15} /> Archiviate ({archivedRooms.length}) {showArchived ? "▲" : "▼"}
          </button>
          {showArchived && archivedRooms.map((r) => (
            <div key={r.id} data-testid={`tk-archived-${r.id}`} className="w-full rounded-xl border border-white/5 bg-[#12161A] p-4 flex items-center gap-2 opacity-80">
              <button onClick={() => navigate(`/tiket/${r.id}`)} className="flex-1 min-w-0 text-left">
                <span className="font-bold">{r.name}</span>
                <span className="ml-2 text-xs text-[#94A3B8]">{r.description || (r.matchday != null ? `G${r.matchday}` : "")} · archiviata</span>
              </button>
              <button data-testid={`tk-unarchive-${r.id}`} onClick={(e) => unarchive(e, r)} title="Ripristina" className="p-2 rounded-md text-[#00D95F] hover:bg-white/5 shrink-0"><ArchiveRestore size={18} /></button>
              <button data-testid={`tk-delete-${r.id}`} onClick={(e) => remove(e, r)} title="Elimina" className="p-2 rounded-md text-[#EF4444] hover:bg-white/5 shrink-0"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
